/**
 * Probability of Simple Events generator (6.DPSR.2.2).
 *
 * P(event) = favorable outcomes / total equally-likely outcomes, written as a
 * reduced fraction. Counts are chosen first and the probability is derived from
 * them, so the scenario and the graded fraction always agree. The Rational is
 * stored reduced; the grader accepts an equivalent fraction such as "3/8".
 *
 * Families:
 *   F1: spinner with colored sectors
 *   F2: bag of marbles / balls
 *   F3: rolling a number cube (die)
 *   F4: drawing a card from a small deck
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "simple-probability" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Total count for spinner/bag contexts. */
  readonly totalMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2", "F3"], totalMax: 8 },
  standard: { families: ["F1", "F2", "F3", "F4"], totalMax: 12 },
  hard: { families: ["F1", "F2", "F3", "F4"], totalMax: 20 },
  challenge: { families: ["F2", "F3", "F4"], totalMax: 25 },
};

export class SimpleProbabilityGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let favorable: number;
      let total: number;
      let promptText: string;

      if (family === "F1") {
        // Spinner with equal sectors, some the target color.
        total = rng.int(4, cfg.totalMax);
        favorable = rng.int(1, total - 1);
        const color = rng.pick(["red", "blue", "green", "yellow"]);
        promptText = `A spinner has ${total} equal sections. ${favorable} of them are ${color}. What is the probability of landing on ${color}?`;
      } else if (family === "F2") {
        const targetColor = rng.pick(["red", "blue", "green"]);
        const otherColor = targetColor === "red" ? "blue" : "red";
        favorable = rng.int(1, Math.max(2, cfg.totalMax - 3));
        const others = rng.int(1, Math.max(2, cfg.totalMax - favorable));
        total = favorable + others;
        promptText = `A bag has ${favorable} ${targetColor} marbles and ${others} ${otherColor} marbles. What is the probability of drawing a ${targetColor} marble?`;
      } else if (family === "F3") {
        // Standard number cube with faces 1-6.
        total = 6;
        const events = [
          { fav: 1, desc: rng.pick(["a 3", "a 5", "a 1"]) },
          { fav: 3, desc: "an odd number" },
          { fav: 3, desc: "an even number" },
          { fav: 2, desc: "a number greater than 4" },
          { fav: 4, desc: "a number less than 5" },
        ];
        const ev = rng.pick(events);
        favorable = ev.fav;
        promptText = `A fair number cube is rolled. What is the probability of rolling ${ev.desc}?`;
      } else {
        // Small deck of numbered/labeled cards.
        total = rng.pick([10, 20, 25, 50]);
        favorable = rng.int(1, total - 1);
        promptText = `A deck has ${total} cards numbered 1 to ${total}. If you draw one card, what is the probability of drawing one of ${favorable} winning cards?`;
      }

      // Guard: probability must be strictly between 0 and 1 (a genuine chance).
      if (favorable <= 0 || favorable >= total) continue;

      const answer: Rational = rat(favorable, total);

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description:
              "Probability = favorable outcomes / total outcomes. Write the fraction and reduce it.",
            latex: `\\frac{${favorable}}{${total}} = ${answer.toLatex()}`,
          },
        ],
        hints: [
          { level: 1, text: "Count the favorable outcomes and the total outcomes." },
          { level: 2, text: "Write favorable over total, then simplify the fraction." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const simpleProbability = new SimpleProbabilityGenerator();
